// src/pages/company-admin/InviteCompanyUserModal.tsx
import React, { useState } from 'react';
import { inviteCompanyUser } from 'src/features/company/api';

type Props = {
    open: boolean;
    onClose: () => void;
    onInvited?: () => void; // 초대 성공 후 리스트 리로드용 콜백
};

const InviteCompanyUserModal: React.FC<Props> = ({ open, onClose, onInvited }) => {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [roleName, setRoleName] = useState<'COMPANY_USER' | 'COMPANY_ADMIN'>('COMPANY_USER');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [resultMessage, setResultMessage] = useState<string | null>(null);

    if (!open) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim()) {
            setErr('초대할 사용자의 이메일을 입력해 주세요.');
            return;
        }

        setSaving(true);
        setErr(null);
        setResultMessage(null);

        try {
            const res = await inviteCompanyUser({
                email: email.trim(),
                name: name.trim() || undefined,
                companyRoleName: roleName,
            });

            setResultMessage(
                [
                    `초대 완료: ${res.user.email} (${res.membership.role})`,
                    res.plainPassword ? `초기 비밀번호: ${res.plainPassword}` : null,
                    res.plainPassword ? '첫 로그인 후 비밀번호를 변경해야 합니다.' : null,
                ]
                    .filter(Boolean)
                    .join('\n'),
            );
            setEmail('');
            setName('');
            if (onInvited) onInvited();
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? '유저 초대 중 오류가 발생했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (saving) return;
        setEmail('');
        setName('');
        setResultMessage(null);
        setErr(null);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-800">회사 사용자 초대</h3>
                    <button type="button" className="text-xs text-gray-500 hover:text-gray-700" onClick={handleClose}>
                        닫기
                    </button>
                </div>

                <p className="text-xs text-gray-500">
                    이메일 주소를 입력하면 사용자를 생성하거나 기존 계정에 회사를 연결합니다.
                </p>

                {err && (
                    <div className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">
                        {err}
                    </div>
                )}

                {resultMessage && (
                    <div className="whitespace-pre-line text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2">
                        {resultMessage}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">이메일</label>
                        <input
                            type="email"
                            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-sky-500"
                            placeholder="user@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={saving}
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">이름 (선택)</label>
                        <input
                            type="text"
                            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-sky-500"
                            placeholder="표시 이름 (미입력 시 이메일 앞부분 사용)"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={saving}
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">회사 역할</label>
                        <select
                            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-sky-500"
                            value={roleName}
                            onChange={(e) => setRoleName(e.target.value as 'COMPANY_USER' | 'COMPANY_ADMIN')}
                            disabled={saving}
                        >
                            <option value="COMPANY_USER">COMPANY_USER (일반 사용자)</option>
                            <option value="COMPANY_ADMIN">COMPANY_ADMIN (관리자)</option>
                        </select>
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            className="px-3 py-1.5 rounded-md border border-gray-300 text-xs text-gray-700 hover:bg-gray-50"
                            onClick={handleClose}
                            disabled={saving}
                        >
                            취소
                        </button>
                        <button
                            type="submit"
                            className="px-3 py-1.5 rounded-md bg-sky-700 text-white text-xs hover:bg-sky-800 disabled:opacity-60"
                            disabled={saving}
                        >
                            {saving ? '초대 중…' : '초대하기'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default InviteCompanyUserModal;
